import React from 'react';

import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {MetricCard, MetricCardGrid} from '@/widgets/MetricCard';
import {colors} from '@app/styles/theme';
import {EMPTY_VALUE} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {taxpayerDetailsModel} from '@entities/taxpayers/detail-model';
import {TaxpayerCriticalStatus, TaxpayerDecType, TaxpayerGapType} from '@entities/taxpayers/types';
import {YearLineChart} from '@pages/OfficerSummary/ui/YearLineChart';
import {ReactComponent as CheckIcon} from '@pages/Relations/assets/check-white.svg';
import {ReactComponent as CircleIcon} from '@pages/Relations/assets/circle-white.svg';
import {ReactComponent as CrosshairIcon} from '@pages/Relations/assets/crosshair-white.svg';
import {ReactComponent as DownIcon} from '@pages/Relations/assets/down-white.svg';
import {ReactComponent as DefaultIcon} from '@pages/Relations/assets/return-white.svg';
import {ReactComponent as SquareIcon} from '@pages/Relations/assets/square-white.svg';
import {ReactComponent as TriangleIcon} from '@pages/Relations/assets/triangle-white.svg';
import {ReactComponent as UpIcon} from '@pages/Relations/assets/up-white.svg';
import {StatusWrapper} from '@shared/ui/PageComponents';

import {
    ActionGroup,
    Chart,
    ChartLegend,
    ChartLegendItem,
    ChartLegendSwatch,
    ContentCard,
    Controls,
    FilterButton,
} from '../styled';

import {declarationStatusColor, formatAmount, riskColor} from './helpers';

const gapTypeLabels: Record<TaxpayerGapType, string> = {
    DECLARATION_NOT_FILED: 'terms.declaration.gap_type.dec_not_filled',
    NDS_NOT_PAID: 'terms.declaration.gap_type.tax_not_paid',
    INVOICE_DIFFER: 'terms.declaration.gap_type.invoice_differ',
    NO_GAP: 'terms.declaration.gap_type.no_gap',
};

const taxpayerTypeLabels: Record<TaxpayerCriticalStatus, string> = {
    TRANSIT_COMPANY: 'terms.taxpayer.transit_company',
    GAP_SOURCE: 'terms.taxpayer.gap_source',
    OTHER: 'terms.taxpayer.other',
};

const declarationTypeLabels: Record<TaxpayerDecType, string> = {
    NOT_FILED: 'terms.declaration.type.not_filled',
    PAYABLE: 'terms.declaration.type.payable',
    DEDUCTION: 'terms.declaration.type.deduction',
    ZERO: 'terms.declaration.type.zero',
};

const taxpayerTypeColor = (value?: TaxpayerCriticalStatus | null) =>
    value === 'GAP_SOURCE'
        ? STATUS_COLOR_LIST.RED
        : value === 'TRANSIT_COMPANY'
        ? STATUS_COLOR_LIST.YELLOW
        : value === 'OTHER'
        ? STATUS_COLOR_LIST.GREEN
        : STATUS_COLOR_LIST.GRAY;

const taxpayerTypeIcon = (value?: TaxpayerCriticalStatus | null) => {
    if (value === 'GAP_SOURCE') return <SquareIcon width={20} height={20} />;
    if (value === 'TRANSIT_COMPANY') return <TriangleIcon width={20} height={20} />;
    if (value === 'OTHER') return <CircleIcon width={20} height={20} />;
    return undefined;
};

const declarationTypeColor = (value?: TaxpayerDecType | null) =>
    value === 'PAYABLE'
        ? STATUS_COLOR_LIST.BLUE
        : value === 'DEDUCTION'
        ? STATUS_COLOR_LIST.GREEN
        : value === 'ZERO'
        ? STATUS_COLOR_LIST.YELLOW
        : STATUS_COLOR_LIST.GRAY;

const declarationTypeIcon = (value?: TaxpayerDecType | null) => {
    if (value === 'NOT_FILED') return <CrosshairIcon width={20} height={20} />;
    if (value === 'PAYABLE') return <UpIcon width={20} height={20} />;
    if (value === 'DEDUCTION') return <DownIcon width={20} height={20} />;
    if (value === 'ZERO') return <CheckIcon width={20} height={20} />;
    return <DefaultIcon width={20} height={20} />;
};

const formatPercent = (value?: number | string | null) => {
    const numericValue = Number(value);
    return value == null || value === '' || Number.isNaN(numericValue)
        ? EMPTY_VALUE
        : `${Math.round(numericValue)}`;
};

const balanceOptions = ['all', 'accrual', 'refund', 'saldo'] as const;
const chartColors = {
    accrual: {backgroundColor: '#FED13D30', borderColor: '#FED13D', legendColor: '#FFF3CE'},
    refund: {backgroundColor: '#4ABD7445', borderColor: '#4ABD74', legendColor: '#4ABD74'},
    saldo: {
        backgroundColor: `${colors.brandPrimary}30`,
        borderColor: colors.brandPrimary,
        legendColor: colors.brandPrimary,
    },
};

export const MainTab = () => {
    const {t} = useTranslation();
    const taxpayer = useUnit(taxpayerDetailsModel);
    const info = taxpayer.state.info;
    const account = taxpayer.state.account;
    const balance = taxpayer.state.balance?.monthTaxMap ?? {};
    const selected = taxpayer.state.selectedBalanceOption;
    const balances = React.useMemo(() => Object.values(balance), [balance]);
    const chartDatasets = React.useMemo(
        () => [
            {
                key: 'accrual',
                label: t('page_taxpayers.table.sum_pay_nds_inv'),
                values:
                    selected === 'all' || selected === 'accrual'
                        ? balances.map((item) => item?.accrual ?? null)
                        : [],
                ...chartColors.accrual,
            },
            {
                key: 'refund',
                label: t('page_taxpayers.table.sum_nds_deduction_inv'),
                values:
                    selected === 'all' || selected === 'refund'
                        ? balances.map((item) => item?.refund ?? null)
                        : [],
                ...chartColors.refund,
            },
            {
                key: 'saldo',
                label: t('page_taxpayer.main.balance'),
                values:
                    selected === 'all' || selected === 'saldo'
                        ? balances.map((item) => item?.saldo ?? null)
                        : [],
                ...chartColors.saldo,
            },
        ],
        [balances, selected, t],
    );

    return (
        <ContentCard>
            <MetricCardGrid style={{gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))'}}>
                <MetricCard
                    iconName="helper"
                    label={t('page_taxpayers.table.sur')}
                    value={
                        <StatusWrapper color={riskColor(info?.surColour)}>
                            {info?.surColour
                                ? t(`terms.declaration.sur.${info.surColour.toLowerCase()}`)
                                : t('no_data')}
                        </StatusWrapper>
                    }
                />
                <MetricCard
                    iconName="taxGap"
                    iconColor={info?.gapType && info.gapType !== 'NO_GAP' ? '#C5A400' : '#4ABD74'}
                    label={t('page_taxpayers.table.gap_type')}
                    value={
                        <StatusWrapper
                            color={
                                info?.gapType && info.gapType !== 'NO_GAP'
                                    ? STATUS_COLOR_LIST.YELLOW
                                    : STATUS_COLOR_LIST.GREEN
                            }
                        >
                            {info?.gapType
                                ? t(gapTypeLabels[info.gapType])
                                : t('terms.declaration.gap_type.no_gap')}
                        </StatusWrapper>
                    }
                />
                <MetricCard
                    icon={taxpayerTypeIcon(info?.transitOrGap)}
                    iconColor={taxpayerTypeColor(info?.transitOrGap)}
                    label={t('page_taxpayers.table.type')}
                    value={
                        <StatusWrapper color={taxpayerTypeColor(info?.transitOrGap)}>
                            {info?.transitOrGap
                                ? t(taxpayerTypeLabels[info.transitOrGap])
                                : EMPTY_VALUE}
                        </StatusWrapper>
                    }
                />
                <MetricCard
                    iconColor={STATUS_COLOR_LIST.RED}
                    iconName="money"
                    label={t('page_taxpayer.main.gap_sum')}
                    value={formatAmount(info?.gapSum)}
                />
                <MetricCard
                    iconName="check"
                    label={t('page_taxpayers.table.dec_status')}
                    value={
                        <StatusWrapper color={declarationStatusColor(info?.isDecFiled)}>
                            {info?.isDecFiled === true
                                ? t('terms.declaration.status.filled')
                                : info?.isDecFiled === false
                                ? t('terms.declaration.status.not_filled')
                                : t('no_data')}
                        </StatusWrapper>
                    }
                />
                <MetricCard
                    icon={declarationTypeIcon(info?.decType)}
                    iconColor={declarationTypeColor(info?.decType)}
                    label={t('page_taxpayers.table.dec_type')}
                    value={
                        <StatusWrapper color={declarationTypeColor(info?.decType)}>
                            {info?.decType ? t(declarationTypeLabels[info.decType]) : EMPTY_VALUE}
                        </StatusWrapper>
                    }
                />
                <MetricCard
                    icon="%"
                    label={t('page_taxpayer.main.deduction')}
                    value={formatPercent(info?.shareDeduction)}
                />
                <MetricCard
                    iconColor={STATUS_COLOR_LIST.RED}
                    iconName="balance"
                    label={t('page_taxpayer.main.debt')}
                    value={formatAmount(account?.debtTotal)}
                />
            </MetricCardGrid>

            <Controls style={{marginTop: 24}}>
                <ActionGroup>
                    {balanceOptions.map((option) => (
                        <FilterButton
                            key={option}
                            type="button"
                            isActive={selected === option}
                            onClick={() => taxpayer.selectedBalanceOptionChanged(option)}
                        >
                            {t(`page_taxpayer.main.${option === 'refund' ? 'payed' : option}`)}
                        </FilterButton>
                    ))}
                </ActionGroup>
                <ChartLegend>
                    <ChartLegendItem>
                        <ChartLegendSwatch color={chartColors.accrual.legendColor} />
                        {t('page_taxpayers.table.sum_pay_nds_inv')}
                    </ChartLegendItem>
                    <ChartLegendItem>
                        <ChartLegendSwatch color={chartColors.refund.legendColor} />
                        {t('page_taxpayers.table.sum_nds_deduction_inv')}
                    </ChartLegendItem>
                    <ChartLegendItem>
                        <ChartLegendSwatch color={chartColors.saldo.legendColor} />
                        {t('page_taxpayer.main.balance')}
                    </ChartLegendItem>
                </ChartLegend>
            </Controls>

            <Chart>
                <YearLineChart datasets={chartDatasets} />
            </Chart>
        </ContentCard>
    );
};
